import PropTypes from 'prop-types'
import React from 'react'
import styled from 'styled-components'
import TransitionLinkUnstyled from 'gatsby-plugin-transition-link'
import posed from 'react-pose'

const headerStates = {
  show: {
    opacity: 1,
  },
  hide: {
    opacity: 0.3,
  },
}

const paneStates = {
  open: {
    background: 'rgba(246, 246, 246, 1)',
    height: 400,
  },
  closed: {
    background: 'rgba(246, 246, 246, 0)',
    height: 300,
    applyAtEnd: { height: '100%' },
  },
}

const coverStates = {
  open: {
    applyAtStart: { display: 'block' },
    opacity: 1,
  },
  closed: {
    applyAtEnd: { display: 'none' },
    opacity: 0,
  },
}

const linksTransitionStates = {
  open: {
    applyAtStart: { display: 'block' },
    opacity: 1,
    y: 0,
  },
  closed: {
    applyAtEnd: { display: 'none' },
    opacity: 0,
    y: -25,
  },
}

const barParent = {
  open: {
    staggerChildren: 100,
    staggerDirection: -1,
  },
  closed: {
    staggerChildren: 50,
    staggerDirection: 1,
  },
}

const barStates = {
  open: {
    opacity: 0,
    y: -5,
    transition: {
      opacity: { ease: 'easeOut', duration: 200 },
    },
  },
  closed: {
    opacity: 1,
    y: 0,
    transition: {
      delay: 150,
      ease: 'easeIn',
      duration: 200,
    },
  },
}

const circleStates = {
  open: {
    opacity: 1,
    y: 0,
    transition: {
      default: { ease: 'linear', duration: 300, delay: 500 },
      opacity: { ease: 'linear', duration: 100, delay: 600 },
    },
  },
  closed: {
    opacity: 0,
    y: 10,
    transition: {
      default: { ease: 'linear', duration: 50 },
    },
  },
}

const StyledHeader = styled(posed.div(headerStates))`
  background: transparent;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 3;
  transition: opacity 0.3s ease-out;
  :hover {
    opacity: 1 !important;
  }
  @media (max-width: 1000px) {
    background: rgba(246, 247, 248, 0.55);
    opacity: 1 !important;
  }
`

const HeaderPane = styled(posed.div(paneStates))`
  padding: 3rem 5rem 0;
  display: flex;
  justify-content: space-between;
  flex-direction: row;
  z-index: 2;
  @media (max-width: 1650px) {
    padding: 2rem 2rem;
  }
  @media (max-width: 1000px) {
    padding-top: 1.2rem;
    padding-bottom: 1.2rem;
  }
`

const HeaderCover = styled(posed.div(coverStates))`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(174, 174, 174, 0.5);
  backdrop-filter: blur(3px);
  z-index: -15;
  opacity: 0;
  display: none;
`

const HeaderRight = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  height: 36px;
`

const TransitionLink = styled(TransitionLinkUnstyled)`
  display: flex;
  text-decoration: none;
  color: #171d20;
  font-size: 22px;
  line-height: 35px;
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', Helvetica Neue,
    Helvetica, Arial, sans-serif;
  text-align: right;
  color: #4f4f4f;
  align-items: center;
  justify-content: flex-end;
  padding: 0.5rem 0;

  :visited {
    color: #4f4f4f;
  }
  &:hover {
    opacity: 0.5;
  }
`

const MenuIcon = styled.svg`
  margin-left: 1.2rem;
  width: 29px;
  height: 29px;
  margin-right: 3px;
  fill: none;
  @media (max-width: 768px) {
    margin-right: -1px;
  }
`

const NavLinks = styled(posed.div(linksTransitionStates))`
  height: 100%;
  /* background-color: rgba(246, 247, 248, 0.5); */
  padding: 0rem;
  margin-top: 2.8rem;
  display: none;
`

const MenuButton = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 36px;
  height: 36px;
  width: 36px;
  justify-content: center;
  fill: #333333;
  :hover {
    cursor: pointer;
  }
  @media (max-width: 600px) {
    height: 30px;
    width: 30px;
  }
`

const Logo = styled.a`
  height: 46px;
  transition: all 0.225s ease-out;
  svg {
    height: 100%;
  }
  :hover {
    opacity: 0.6;
  }
  @media (max-width: 600px) {
    height: 26px;
    margin-top: 6px;
  }
`

const StyledLogo = styled.path`
  stroke-width: 1;
  opacity: 1;
  stroke: #333333;
  stroke-width: 1;
  margin: 0 0 0 0;
  fill-opacity: 1;
  fill: #333333;
`

const MenuBars = styled(posed.svg(barParent))`
  height: 100%;
  width: 100%;
  overflow: visible;
  @media (hover: hover) {
    ${MenuButton}:hover & {
      opacity: 0.6;
    }
  }
`

const CircleBarOne = styled(posed.path(barStates))``
const CircleBarTwo = styled(posed.path(barStates))``
const CircleBarThree = styled(posed.path(barStates))``
const CircleCross = styled(posed.path(circleStates))`
  fill-rule: evenodd;
  clip-rule: evenodd;
  @media (hover: hover) {
    ${MenuButton}:hover & {
      fill: red;
    }
  }
`

class Header extends React.Component {
  constructor(props) {
    super(props)
    let path
    if (typeof window !== `undefined`) {
      path = window.location.pathname.replace(/\//g, '')
      if (path === '') {
        path = 'home'
      }
    } else {
      path = 'home'
    }

    this.state = {
      position: path,
      hoverScale: 'initial',
      mouseEntered: false,
      latestPosition: path,
      headerOnScroll: 'show',
      headerOpen: false,
    }
    this.mouseEnter = this.mouseEnter.bind(this)
    this.mouseLeave = this.mouseLeave.bind(this)
    this.pressUp = this.pressUp.bind(this)
    this.pressDown = this.pressDown.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.toggleHeader = this.toggleHeader.bind(this)
  }

  componentDidMount() {
    window.addEventListener('scroll', this.handleScroll)
  }

  toggleHeader() {
    this.setState({ headerOpen: !this.state.headerOpen })
  }

  handleScroll() {
    if (window.scrollY > 200) {
      this.setState({ headerOnScroll: 'hide' })
    } else {
      this.setState({ headerOnScroll: 'show' })
    }
  }

  mouseEnter() {
    this.setState({ hoverScale: 'samesame', mouseEntered: true })
  }

  mouseLeave() {
    if (this.state.latestPosition !== this.state.position) {
      this.setState({ position: this.state.latestPosition })
    }
    this.setState({ hoverScale: 'initial', mouseEntered: false })
  }

  pressDown() {
    this.setState({ hoverScale: 'press' })
  }

  pressUp() {
    this.setState({ hoverScale: 'samesame' })
  }

  setPosition() {
    this.toggleHeader()
  }

  getTransitionStates(status) {
    if (['entering', 'entered'].includes(status)) {
      return ['visible', 'logoChain']
    } else if (['exiting'].includes(status)) {
      return 'exitedState'
    } else if (['exited'].includes(status)) {
      return 'startState'
    }
  }

  render() {
    return (
      <StyledHeader pose={this.state.headerOnScroll}>
        <HeaderPane pose={this.state.headerOpen ? 'open' : 'closed'}>
          <Logo href={'/'} title="Monogram">
            <svg viewBox="0 0 2093 868">
              <StyledLogo d="M433.81 867.62C673.397 867.62 867.62 673.397 867.62 433.81C867.62 194.223 673.397 0 433.81 0C194.223 0 0 194.223 0 433.81C0 673.397 194.223 867.62 433.81 867.62ZM1308.89 287.079L967 628.921L1188.11 850L1530 508.158L1871.89 850L2093 628.921L1751.11 287.079L1530 66L1308.89 287.079Z" />
            </svg>
          </Logo>
          <HeaderRight>
            <MenuButton onClick={this.toggleHeader}>
              <MenuBars
                width="34"
                height="26"
                viewBox="0 0 34 26"
                xmlns="http://www.w3.org/2000/svg"
                pose={this.state.headerOpen}
              >
                <CircleBarOne d="M30 21C32.2091 21 34 19.2091 34 17C34 14.7909 32.2091 13 30 13C27.7909 13 26 14.7909 26 17C26 19.2091 27.7909 21 30 21Z" />
                <CircleBarTwo d="M17 21C19.2091 21 21 19.2091 21 17C21 14.7909 19.2091 13 17 13C14.7909 13 13 14.7909 13 17C13 19.2091 14.7909 21 17 21Z" />
                <CircleBarThree d="M4 21C6.20914 21 8 19.2091 8 17C8 14.7909 6.20914 13 4 13C1.79086 13 0 14.7909 0 17C0 19.2091 1.79086 21 4 21Z" />
                <g>
                  <CircleCross
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M17 34C7.61116 34 -1.10269e-06 26.3888 -1.10269e-06 17C-1.10269e-06 7.61115 7.61116 0 17 0C26.3888 0 34 7.61115 34 17C34 26.3888 26.3888 34 17 34ZM19.8621 17L25.987 23.1249L23.1249 25.987L17 19.8621L10.8751 25.987L8.01302 23.1249L14.1379 17L8.013 10.8751L10.8751 8.01301L17 14.1379L23.1249 8.01301L25.987 10.8751L19.8621 17Z"
                    fill="#333333"
                    opacity={0}
                  />{' '}
                </g>
              </MenuBars>
            </MenuButton>
            <NavLinks
              onMouseEnter={this.mouseEnter}
              onMouseLeave={this.mouseLeave}
              pose={this.state.headerOpen ? 'open' : 'closed'}
            >
              <div onMouseDown={this.pressDown} onMouseUp={this.pressUp}>
                <TransitionLink
                  to="/"
                  exit={{
                    length: 0.6,
                    zIndex: 2,
                    trigger: () => this.toggleHeader(),
                  }}
                  entry={{
                    delay: 0.6,
                    length: 0.4,
                  }}
                >
                  Work
                  <MenuIcon
                    viewBox="0 0 35 35"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.6206 5.59464C16.9676 4.84736 18.03 4.84736 18.377 5.59464L21.5241 12.3712C21.6652 12.675 21.9533 12.8843 22.2858 12.9246L29.7032 13.8236C30.5211 13.9227 30.8494 14.9331 30.246 15.4941L24.7736 20.5812C24.5282 20.8093 24.4182 21.1479 24.4826 21.4767L25.9198 28.8088C26.0782 29.6173 25.2188 30.2418 24.4987 29.8412L17.9696 26.2087C17.6768 26.0458 17.3207 26.0458 17.028 26.2087L10.4988 29.8412C9.77882 30.2418 8.91934 29.6173 9.07782 28.8088L10.5149 21.4767C10.5794 21.1479 10.4693 20.8093 10.224 20.5812L4.75161 15.4941C4.14814 14.9331 4.47644 13.9227 5.29438 13.8236L12.7118 12.9246C13.0443 12.8843 13.3324 12.675 13.4735 12.3712L16.6206 5.59464Z"
                      stroke="#333333"
                      stroke-width="2"
                    />
                    fill="#333333" />
                  </MenuIcon>
                </TransitionLink>
              </div>
              <div onMouseDown={this.pressDown} onMouseUp={this.pressUp}>
                <TransitionLink
                  to="/notes/"
                  exit={{
                    length: 0.6,
                    zIndex: 2,
                    trigger: () => this.toggleHeader(),
                  }}
                  entry={{
                    delay: 0.6,
                    length: 0.4,
                  }}
                >
                  Notes
                  <MenuIcon
                    viewBox="0 0 35 35"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.55078 7.5C5.55078 6.94772 5.9985 6.5 6.55078 6.5H28.4524C29.0047 6.5 29.4524 6.94772 29.4524 7.5C29.4524 8.05228 29.0047 8.5 28.4524 8.5H6.55078C5.9985 8.5 5.55078 8.05228 5.55078 7.5Z"
                      fill="#333333"
                    />
                    <path
                      d="M5.55078 27.5C5.55078 26.9477 5.9985 26.5 6.55078 26.5H24.4524C25.0047 26.5 25.4524 26.9477 25.4524 27.5C25.4524 28.0523 25.0047 28.5 24.4524 28.5H6.55078C5.9985 28.5 5.55078 28.0523 5.55078 27.5Z"
                      fill="#333333"
                    />
                    <path
                      d="M5.55078 22.5C5.55078 21.9477 5.9985 21.5 6.55078 21.5H26.4524C27.0047 21.5 27.4524 21.9477 27.4524 22.5C27.4524 23.0523 27.0047 23.5 26.4524 23.5H6.55078C5.9985 23.5 5.55078 23.0523 5.55078 22.5Z"
                      fill="#333333"
                    />
                    <path
                      d="M5.55078 17.5C5.55078 16.9477 5.9985 16.5 6.55078 16.5H27.4524C28.0047 16.5 28.4524 16.9477 28.4524 17.5C28.4524 18.0523 28.0047 18.5 27.4524 18.5H6.55078C5.9985 18.5 5.55078 18.0523 5.55078 17.5Z"
                      fill="#333333"
                    />
                    <path
                      d="M5.55078 12.5C5.55078 11.9477 5.9985 11.5 6.55078 11.5H23.4524C24.0047 11.5 24.4524 11.9477 24.4524 12.5C24.4524 13.0523 24.0047 13.5 23.4524 13.5H6.55078C5.9985 13.5 5.55078 13.0523 5.55078 12.5Z"
                      fill="#333333"
                    />
                  </MenuIcon>
                </TransitionLink>
              </div>
              <div onMouseDown={this.pressDown} onMouseUp={this.pressUp}>
                <TransitionLink
                  to="/photography/"
                  exit={{
                    length: 0.6,
                    zIndex: 2,
                    trigger: () => this.toggleHeader(),
                  }}
                  entry={{
                    delay: 0.6,
                    length: 0.4,
                  }}
                >
                  Photography
                  <MenuIcon
                    viewBox="0 0 35 35"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M19.8477 9.66691C19.8477 10.2192 20.2954 10.6669 20.8477 10.6669C21.4 10.6669 21.8477 10.2192 21.8477 9.66691H19.8477ZM10.0381 22.9287C10.5903 22.9287 11.0381 22.481 11.0381 21.9287C11.0381 21.3765 10.5903 20.9287 10.0381 20.9287V22.9287ZM3.19534 20.0534L2.26826 20.4282L2.26826 20.4282L3.19534 20.0534ZM4.85193 21.71L5.22681 20.7829L5.22681 20.7829L4.85193 21.71ZM18.9723 4.27639L19.3472 3.34932L19.3472 3.34932L18.9723 4.27639ZM20.6289 5.93299L19.7018 6.30786L19.7018 6.30786L20.6289 5.93299ZM19.8711 13.9932H26.1953V11.9932H19.8711V13.9932ZM31.1953 18.9932V25.3174H33.1953V18.9932H31.1953ZM26.1953 30.3174H19.8711V32.3174H26.1953V30.3174ZM14.8711 25.3174V18.9932H12.8711V25.3174H14.8711ZM19.8711 30.3174C18.4286 30.3174 17.4474 30.3153 16.7125 30.2165C16.0065 30.1216 15.6795 29.9542 15.4569 29.7316L14.0427 31.1458C14.6988 31.8019 15.5182 32.0739 16.446 32.1986C17.3451 32.3195 18.4852 32.3174 19.8711 32.3174V30.3174ZM12.8711 25.3174C12.8711 26.7033 12.869 27.8434 12.9899 28.7425C13.1146 29.6703 13.3866 30.4897 14.0427 31.1458L15.4569 29.7316C15.2343 29.509 15.067 29.182 14.972 28.476C14.8732 27.7411 14.8711 26.7599 14.8711 25.3174H12.8711ZM31.1953 25.3174C31.1953 26.7599 31.1932 27.7411 31.0944 28.476C30.9995 29.182 30.8321 29.509 30.6095 29.7316L32.0238 31.1458C32.6799 30.4897 32.9518 29.6703 33.0766 28.7425C33.1975 27.8434 33.1953 26.7033 33.1953 25.3174H31.1953ZM26.1953 32.3174C27.5813 32.3174 28.7213 32.3195 29.6204 32.1986C30.5482 32.0739 31.3676 31.8019 32.0238 31.1458L30.6095 29.7316C30.387 29.9542 30.0599 30.1216 29.3539 30.2165C28.6191 30.3153 27.6378 30.3174 26.1953 30.3174V32.3174ZM26.1953 13.9932C27.6378 13.9932 28.6191 13.9953 29.3539 14.0941C30.0599 14.189 30.387 14.3564 30.6095 14.579L32.0238 13.1648C31.3676 12.5086 30.5482 12.2367 29.6204 12.1119C28.7213 11.9911 27.5813 11.9932 26.1953 11.9932V13.9932ZM33.1953 18.9932C33.1953 17.6072 33.1975 16.4672 33.0766 15.5681C32.9518 14.6403 32.6799 13.8209 32.0238 13.1648L30.6095 14.579C30.8321 14.8015 30.9995 15.1286 31.0944 15.8346C31.1932 16.5694 31.1953 17.5507 31.1953 18.9932H33.1953ZM19.8711 11.9932C18.4852 11.9932 17.3451 11.9911 16.446 12.1119C15.5182 12.2367 14.6988 12.5086 14.0427 13.1648L15.4569 14.579C15.6795 14.3564 16.0065 14.189 16.7125 14.0941C17.4474 13.9953 18.4286 13.9932 19.8711 13.9932V11.9932ZM14.8711 18.9932C14.8711 17.5507 14.8732 16.5694 14.972 15.8346C15.067 15.1286 15.2343 14.8015 15.4569 14.579L14.0427 13.1648C13.3866 13.8209 13.1146 14.6403 12.9899 15.5681C12.869 16.4672 12.8711 17.6072 12.8711 18.9932H14.8711ZM8.97656 5.05762H16.3799V3.05762H8.97656V5.05762ZM3.97656 17.461V10.0576H1.97656V17.461H3.97656ZM19.8477 8.5254V9.66691H21.8477V8.5254H19.8477ZM10.0381 20.9287H7.44434V22.9287H10.0381V20.9287ZM1.97656 17.461C1.97656 18.1314 1.97604 18.6832 2.00613 19.1338C2.0368 19.593 2.10243 20.0181 2.26826 20.4282L4.12241 19.6785C4.06947 19.5476 4.02571 19.3603 4.00169 19.0005C3.97708 18.632 3.97656 18.1581 3.97656 17.461H1.97656ZM7.44434 20.9287C6.74717 20.9287 6.27329 20.9282 5.90481 20.9036C5.54505 20.8796 5.35774 20.8358 5.22681 20.7829L4.47705 22.637C4.88716 22.8029 5.31226 22.8685 5.77155 22.8992C6.22212 22.9293 6.77393 22.9287 7.44434 22.9287V20.9287ZM2.26826 20.4282C2.67473 21.4334 3.47186 22.2306 4.47706 22.637L5.22681 20.7829C4.72421 20.5797 4.32565 20.1811 4.12241 19.6785L2.26826 20.4282ZM16.3799 5.05762C17.0771 5.05762 17.551 5.05814 17.9194 5.08274C18.2792 5.10677 18.4665 5.15052 18.5974 5.20347L19.3472 3.34932C18.9371 3.18349 18.512 3.11786 18.0527 3.08719C17.6021 3.0571 17.0503 3.05762 16.3799 3.05762V5.05762ZM21.8477 8.5254C21.8477 7.85499 21.8482 7.30318 21.8181 6.85261C21.7874 6.39332 21.7218 5.96822 21.556 5.55811L19.7018 6.30786C19.7548 6.43879 19.7985 6.6261 19.8226 6.98587C19.8472 7.35435 19.8477 7.82822 19.8477 8.5254H21.8477ZM18.5974 5.20347C19.1 5.4067 19.4986 5.80526 19.7018 6.30786L21.556 5.55811C21.1495 4.55291 20.3524 3.75579 19.3472 3.34932L18.5974 5.20347ZM8.97656 3.05762C7.59062 3.05762 6.45056 3.05549 5.5515 3.17637C4.62367 3.30111 3.80424 3.57308 3.14814 4.22919L4.56235 5.6434C4.78492 5.42083 5.11194 5.25346 5.81799 5.15853C6.55281 5.05974 7.53408 5.05762 8.97656 5.05762V3.05762ZM3.97656 10.0576C3.97656 8.61514 3.97869 7.63387 4.07748 6.89905C4.17241 6.193 4.33978 5.86597 4.56235 5.6434L3.14814 4.22919C2.49203 4.8853 2.22006 5.70472 2.09531 6.63256C1.97444 7.53162 1.97656 8.67168 1.97656 10.0576H3.97656Z"
                      fill="#333333"
                    />
                    />
                  </MenuIcon>
                </TransitionLink>
              </div>
              <div onMouseDown={this.pressDown} onMouseUp={this.pressUp}>
                <TransitionLink
                  to="/bookshelf/"
                  exit={{
                    length: 0.6,
                    zIndex: 2,
                    trigger: () => this.toggleHeader(),
                  }}
                  entry={{
                    delay: 0.6,
                    length: 0.4,
                  }}
                >
                  Bookshelf
                  <MenuIcon
                    viewBox="0 0 35 35"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.1054 28.9207C11.0494 28.9789 10.9959 29.0346 10.9447 29.0879C10.4401 29.6134 10.1603 29.9048 9.93961 30.0842C9.77491 30.2182 9.74243 30.2091 9.73117 30.2059C9.72993 30.2056 9.72894 30.2053 9.72806 30.2053C9.58637 30.2053 9.54033 30.1645 9.51512 30.1355C9.47596 30.0904 9.36328 29.9182 9.36328 29.4628V7.84281C9.36328 6.63494 9.64012 5.90262 10.0176 5.47839C10.3762 5.07554 10.9652 4.79492 11.9624 4.79492H23.0377C24.0422 4.79492 24.63 5.07624 24.9862 5.47746C25.362 5.90082 25.6368 6.63267 25.6368 7.84281V29.4628C25.6368 29.9182 25.5242 30.0904 25.485 30.1355C25.4598 30.1645 25.4138 30.2053 25.2721 30.2053C25.2054 30.2053 25.1539 30.193 25.0619 30.1301C24.9361 30.0441 24.7957 29.9064 24.5478 29.6454L24.5362 29.633L24.4899 29.5836L24.314 29.396L23.684 28.7244L21.7561 26.6692C20.3451 25.1652 18.8984 23.6236 18.7751 23.4936C18.4931 23.1963 18.0845 22.8467 17.5061 22.8467C16.9304 22.8467 16.5374 23.1634 16.2241 23.4945C16.0966 23.6293 14.8155 24.9874 13.5675 26.3104L11.8666 28.1136L11.3118 28.7018L11.1568 28.8661L11.1161 28.9093L11.1056 28.9204L11.1054 28.9207Z"
                      stroke="#333333"
                      stroke-width="2"
                    />
                  </MenuIcon>
                </TransitionLink>
              </div>
            </NavLinks>
          </HeaderRight>
        </HeaderPane>
        <HeaderCover
          pose={this.state.headerOpen ? 'open' : 'closed'}
          onClick={this.toggleHeader}
        />
      </StyledHeader>
    )
  }
}

Header.propTypes = {
  siteTitle: PropTypes.string,
}

Header.defaultProps = {
  siteTitle: '',
}

export default Header
